"use client";

import { useScroll, useMotionValueEvent } from "motion/react";
import React, { createContext, useContext, useRef, useState } from "react";

import { MODAL_STICKY_DETECTION_HEADER_OFFSET } from "./consts";
import { useModalScrollContext } from "./ModalScrollContext";

export const ModalHeaderContext: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const value = useInitContext();
  return <Context value={value}>{children}</Context>;
};

export const useModalHeaderContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("ModalContext not found");
  }

  return context;
};

const Context = createContext<ReturnType<typeof useInitContext> | undefined>(
  undefined
);

function useInitContext() {
  const inlineRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);

  const { showStickyHeader } = useShowStickyHeader({
    inlineRef,
    stickyRef,
  });

  return { inlineRef, stickyRef, showStickyHeader };
}

const useShowStickyHeader = ({
  inlineRef,
  stickyRef,
}: {
  inlineRef: React.RefObject<HTMLDivElement | null>;
  stickyRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { scrollAreaRef } = useModalScrollContext();
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  const { scrollY } = useScroll({
    container: scrollAreaRef,
    target: inlineRef,
  });

  useMotionValueEvent(scrollY, "change", (latest) => {
    const inlineHeight = inlineRef?.current?.offsetHeight ?? 0;
    const stickyHeight = stickyRef.current?.offsetHeight ?? 0;
    const maxScroll = Math.max(
      inlineHeight - stickyHeight - MODAL_STICKY_DETECTION_HEADER_OFFSET,
      0
    );
    setShowStickyHeader(latest > maxScroll);
  });

  return { showStickyHeader };
};
